// Considered: StatusSparklineAtom, HistoryStripMolecule — both render discrete equal-width slots;
// this needs PROPORTIONAL intervals (flex-grown by duration), transition markers and a future
// remainder. See spec Reuse Audit (specs/stream-trajectory-tools.md § New components — justified).
//
// Source: design/metaspace-tools/metaspace-trajectory-v2.jsx:150-166 (the Health track's band bar
// + legend row). Renders as color bands from `--state-*` tokens with text labels — state is never
// conveyed by color alone (README §"State model").

import React from 'react';
import { useTranslation } from 'localization';
import InspectableMolecule from '../../molecules/Inspectable/InspectableMolecule';
import type { TrajectoryBand } from 'types/trajectory';
import type { InspectorPayload } from 'types/inspector';
import './StateBandAtom.scss';

const STATE_COLOR_VAR: Record<string, string> = {
  on_track: 'var(--state-on-track)',
  at_risk: 'var(--state-at-risk)',
};

function stateColor(state: string): string {
  return STATE_COLOR_VAR[state] ?? 'var(--track-future)';
}

/** Hours between `from` and `to` (or `now` for the open band) — the flex-grow unit. */
function bandGrowHours(band: TrajectoryBand, now: string): number {
  const from = new Date(band.from).getTime();
  const to = new Date(band.to ?? now).getTime();
  return Math.max(1, Math.round((to - from) / (1000 * 60 * 60)));
}

export interface StateBandAtomProps {
  /** Chronologically-ordered bands; the last band's `to: null` marks the currently-open interval. */
  bands: TrajectoryBand[];
  /** The open band's effective end. Defaults to render time. */
  now?: string;
  /** Display label per state; defaults to the raw state string. */
  stateLabel?: (state: string) => string;
  nowLabel?: string;
  buildBandPayload?: (band: TrajectoryBand, index: number) => InspectorPayload;
  buildTransitionPayload?: (band: TrajectoryBand) => InspectorPayload;
  /**
   * Fires for a band-interval click. The raw `band`/`index` are passed alongside the built
   * payload so the caller can also scope the Stream to this aspect × interval without having to
   * decode `from`/`to` back out of the payload's `address`/`rows`.
   */
  onBandInspect?: (payload: InspectorPayload, band: TrajectoryBand, index: number) => void;
  /** Fires for a transition-marker click — opens the causing chord, does not scope anything. */
  onTransitionInspect?: (payload: InspectorPayload, band: TrajectoryBand) => void;
  className?: string;
}

const StateBandAtom: React.FC<StateBandAtomProps> = ({
  bands,
  now = new Date().toISOString(),
  stateLabel = (state) => state,
  nowLabel,
  buildBandPayload,
  buildTransitionPayload,
  onBandInspect,
  onTransitionInspect,
  className,
}) => {
  const { t } = useTranslation();
  const resolvedNowLabel = nowLabel ?? t('trajectory.stateBand.now', { defaultValue: 'now' });
  const isOpen = bands.length > 0 && bands[bands.length - 1].to === null;

  return (
    <div className={['state-band-atom', className].filter(Boolean).join(' ')}>
      <div className="state-band-atom__bar">
        {bands.map((band, index) => {
          const grow = bandGrowHours(band, now);
          const bandPayload = buildBandPayload?.(band, index);
          const transitionPayload = band.transitionSeq ? buildTransitionPayload?.(band) : undefined;
          const fromLabel = new Date(band.from).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
          const toLabel = band.to ? new Date(band.to).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : resolvedNowLabel;

          return (
            <React.Fragment key={`${band.state}-${band.from}`}>
              {transitionPayload && (
                <InspectableMolecule
                  payload={transitionPayload}
                  onInspect={(payload) => onTransitionInspect?.(payload, band)}
                  peek={t('trajectory.stateBand.turnedPeek', { state: stateLabel(band.state), date: fromLabel, defaultValue: 'Turned {{state}} — {{date}}' })}
                  block
                  aria-label={t('trajectory.stateBand.transitionAria', { state: stateLabel(band.state), defaultValue: 'Transition to {{state}}' })}
                  className="state-band-atom__transition"
                >
                  <span className="state-band-atom__transition-mark" />
                </InspectableMolecule>
              )}
              <InspectableMolecule
                payload={bandPayload}
                onInspect={(payload) => onBandInspect?.(payload, band, index)}
                peek={t('trajectory.stateBand.bandPeek', { state: stateLabel(band.state), from: fromLabel, to: toLabel, defaultValue: '{{state}} · {{from}} – {{to}}' })}
                block
                grow={grow}
                aria-label={t('trajectory.stateBand.bandAria', { state: stateLabel(band.state), defaultValue: '{{state}} interval' })}
                className="state-band-atom__segment"
              >
                <span className="state-band-atom__fill" style={{ background: stateColor(band.state) }} />
              </InspectableMolecule>
            </React.Fragment>
          );
        })}
        {isOpen && <span className="state-band-atom__future" aria-hidden="true" />}
      </div>
      <div className="state-band-atom__legend">
        {bands.map((band, index) => (
          <span
            key={`legend-${band.state}-${band.from}`}
            className="state-band-atom__legend-item"
            style={{ flex: `${bandGrowHours(band, now)} 1 0%`, color: stateColor(band.state) }}
          >
            {index === 0
              ? stateLabel(band.state)
              : `${stateLabel(band.state)} · ${new Date(band.from).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
          </span>
        ))}
        <span className="state-band-atom__legend-now">{resolvedNowLabel}</span>
      </div>
    </div>
  );
};

StateBandAtom.displayName = 'StateBandAtom';

export default StateBandAtom;
